import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { Divider, Grid, Box, Typography } from '@mui/material';
import { HasPermission } from 'Core/security';
import { GetFormatForMoment, cleanString, cleanLicensePlate } from 'Core/data/Helpers';
import {  Input as InputComponent } from "Components";

import {
  Form,
  Input,
  Select,
  InputNumeric,
  DatePicker,
  Tooltip,
  Icons,
  Switch
} from '@gpstrackit/gtc-react-controls';
import { fleetModalAssetTypeSearchEntityLists, fleetModalDecodeVin } from "../redux/slice";

const sections = Object.freeze({
  LICENSE_PLATE: 'licensePlate',
  MAINTENANCE: 'maintenance',
  REGISTRATION: 'registration',
  CHASSIS_SERIAL_NUMBER: 'chassisSerialNumber',
  ENGINE_SERIAL_NUMBER: 'engineSerialNumber',
  VEHICLE_ID: 'vehicleId',
  MPG: 'mpg',
  EVIR: 'evir',
  ZONAR_ELD: 'zonarEld',
});

const CounterString = ({ string, maxLength }) => {
  const stringValue = String(string ?? '');
  return (
    <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
      <Typography style={{ fontSize: 12, color: '#666' }}>
        {stringValue?.length} / {maxLength}
      </Typography>
    </Box>
  );
};




const Asset = (props) => {
    const { messages } = useIntl();
    const { formData, setFormData, unitFormRef, changes, setChanges, onEcuChange } = props;
    const dispatch = useDispatch();

    //Redux state
    const { loadingEntityLists, entityLists, loadingAssetSubtypes, assetSubtypes , assetTypeEntityLists, loadingDecodeVin, deviceVin} = useSelector((state) => state.fleetModalUpdateUnitRedux);
    const unit = useSelector((state) => state.fleetModalUpdateUnitRedux.unit);
    const user = useSelector((state) => state.securityRedux.user);
    const nextYear = new Date().getFullYear() + 1;

    //Local state
    const [countryOptions, setCountryOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);
    const manualVinRef = useRef('');

    const itemRefs = useRef([]);

    const registerItem = useCallback((el) => {
      if (!el) return;
      if (!itemRefs.current.includes(el)) itemRefs?.current.push(el);
    }, []);

    const didWrapSubmit = useRef(false);

useEffect(() => {
  if (didWrapSubmit.current) return;
  if (!unitFormRef || typeof unitFormRef.submit !== 'function') return;

  didWrapSubmit.current = true;

  const originalSubmit = unitFormRef.submit;

  unitFormRef.submit = (...args) => {
    itemRefs.current.forEach((r) => r?.validate && r.validate());
    return originalSubmit(...args);
  };

  return () => {
    unitFormRef.submit = originalSubmit;
    didWrapSubmit.current = false;
  };
}, [unitFormRef]);

    useEffect(() => {
        if (!Array.isArray(entityLists) || entityLists.length === 0) return;
        const isEntity = (type) => (item) => item?.entityName === type;
        const countries = entityLists.filter(isEntity("COUNTRY"));
        const states = entityLists.filter(isEntity("STATE"));
        setCountryOptions([{ entityId: "NONE", label: messages['fleetModalUpdateUnit_none'] }, ...countries]);
        setStateOptions([{ entityId: "NONE", label: messages['fleetModalUpdateUnit_none'], parentId: "NONE" }, ...states]);
    }, [entityLists])


     useEffect(() => {
            if (!unit) return;

            const noConfig =
                !assetTypeEntityLists ||
                (Array.isArray(assetTypeEntityLists?.items) && assetTypeEntityLists.items.length === 0) ||
                (Array.isArray(assetTypeEntityLists) && assetTypeEntityLists.length === 0);
    
            if (!noConfig) return;
    
            const assetTypeLabel =
                unit.asset?.assetType?.label ||
                entityLists?.find(it => it?.entityName === "AssetType" && it?.entityId === unit.asset?.assetTypeId)?.label;

            if (assetTypeLabel) {
                dispatch(fleetModalAssetTypeSearchEntityLists({ label: assetTypeLabel }));
            }
        }, [unit, entityLists, assetTypeEntityLists, dispatch]);

        const setForm = useCallback(
            (partial) => {
              setFormData((prev) => ({ ...prev, ...partial }));
              unitFormRef.setValues(partial);
            },
          [unitFormRef, setFormData]
        );
            
        const change = useCallback(
          (field, value) => {
            setChanges((prev) => ({ ...prev, [field]: true }));
            if (field === "vin" && !formData?.useEcuVin) {
              manualVinRef.current = value || "";
            }
            setForm({ [field]: value });
          },
          [setChanges, setForm, formData?.useEcuVin]
        );

        const clearVinDependentFields = useCallback(() => {
          setChanges((prev) => ({ ...prev, make: true, model: true, year: true, engine: true }));
          setTimeout(() => {
            setForm({
              make: "",
              model: "",
              year: null,
              engine: null,
            }), 500});
        }, [setChanges, setForm]);

        useEffect(() => {
          if (loadingDecodeVin) return;
          if (!deviceVin) return;

          if (deviceVin?.error) {
            clearVinDependentFields();
            return;
          }

          const patch = {
            make: cleanString(deviceVin?.Make ?? ""),
            model: cleanString(deviceVin?.Model ?? ""),
            year: deviceVin?.ModelYear ? Number(deviceVin.ModelYear) : null,
            engine: deviceVin?.DisplacementL ? Number(deviceVin.DisplacementL) : null,
          };

          setChanges((prev) => ({ ...prev, make: true, model: true, year: true, engine: true }));
          setForm(patch);
        }, [
          loadingDecodeVin,
          deviceVin,
          clearVinDependentFields,
          setForm,
          setChanges,
        ]);


          
      useEffect(() => {
        if (formData?.isActive?.id != null) return; 

        const active = unit?.asset?.isActive === false
          ? { id: false, name: messages['manage_createEditAsset_inactive'] }
          : { id: true,  name: messages['manage_createEditAsset_active'] };

        const assetVin = unit?.asset?.vin || '';
        const ecuVin = unit?.lastReading?.ecuVin || unit?.asset?.ecuVin || '';
        const vin = unit?.useEcuVin ? (ecuVin || assetVin) : assetVin;

        if (!unit?.useEcuVin && assetVin) {
          manualVinRef.current = assetVin;
        }
        setFormData((prev) => ({ ...prev, isActive: active, vin }));
        unitFormRef.setValues({ isActive: active, vin });
      }, [unit?.asset?.isActive, messages, formData?.isActive?.id]);
                  
            
              const optionsIds = useCallback(
                (type) => {
                  const item =
                    assetTypeEntityLists?.items?.[0] || assetTypeEntityLists?.[0] || null;
                  if (!item) return [];
            
                  let arr = [];
                  try {
                    arr = Array.isArray(item.jsonValue)
                      ? item.jsonValue
                      : item.jsonValue
                      ? JSON.parse(item.jsonValue)
                      : [];
                  } catch {
                    arr = [];
                  }
                  if (!type) return [];
                  if (type === 'visible') {
                    return arr.filter((x) => x && x.section && x.visible === true).map((x) => x.section);
                  } else {
                    return arr
                      .filter((x) => x && x.section && Array.isArray(x.requiredFields) && x.requiredFields.length > 0)
                      .flatMap((x) => x.requiredFields);
                  }
                },
                [assetTypeEntityLists]
              );
            
              const visibleSections = useMemo(() => optionsIds('visible'), [optionsIds]);
              const requiredFields = useMemo(() => optionsIds('required'), [optionsIds]);
            
              const isVisible = useCallback((section) => visibleSections.includes(section), [visibleSections]);
              const isRequired = useCallback((field) => requiredFields.includes(field), [requiredFields]);
            
              const registrationStateKey = formData?.registrationCountry?.entityId || 'no-country';

    

    return (
        <div>
            <Form.Item
            ref={registerItem}
            name="externalId"
            label={messages['manage_createEditAsset_externalId']}
            className="unit-unit-asset-formItem"
            >
            <Input
                id="externalId"
                type="text"
                placeholder={messages['manage_createEditAsset_enterExternalId']}
                onChange={(value) => {
                    const cleanedValue = cleanString(value);
                    change('externalId', cleanedValue);
                    if (cleanedValue !== value) {
                        setTimeout(() => {
                            unitFormRef.setValues({ externalId: cleanedValue });
                        }, 100);
                    }
                }}
                maxLength={50}
            />
            </Form.Item>
            <CounterString string={formData?.externalId || ''} maxLength={50} />
    
            <Form.Item
            ref={registerItem}
            name="label"
            label={messages['manage_createEditAsset_label']}
            rules={[{ required: true, message: messages['required_field'] }]}
            className="unit-asset-formItem"
            >
            <Input
                id="label"
                type="text"
                placeholder={messages['manage_createEditAsset_placeholderLabel']}
                onChange={(value) => {
                    const cleanedValue = cleanString(value);
                    change('label', cleanedValue);
                    if (cleanedValue !== value) {
                        setTimeout(() => {
                            unitFormRef.setValues({ label: cleanedValue });
                        }, 100);
                    }
                }}
                maxLength={50}
            />
            </Form.Item>
            <CounterString string={formData?.label || ''} maxLength={50} />
    
            <Form.Item ref={registerItem} name="isActive" label={messages['manage_createEditAsset_status']}>
            <Select
                id="isActive"
                options={[
                { id: true, name: messages['manage_createEditAsset_active'] },
                { id: false, name: messages['manage_createEditAsset_inactive'] },
                ]}
                placeholder={messages['select']}
                valueField="id"
                textField="name"
                style={{ width: '100%', fontFamily: '"Outfit", sans-serif' }}
                onChange={(value) => change('isActive', value)}
            />
            </Form.Item>
    
            <Form.Item
            ref={registerItem}
            name="assetType"
            label={messages['manage_createEditAsset_assetType']}
            rules={[{ required: HasPermission(user, 'evir'), message: messages['required_field'] }]}
            >
            <Select
                id="assetType"
                options={entityLists?.filter((item) => item?.entityName === 'AssetType') || []}
                placeholder={messages['select']}
                valueField="entityId"
                textField="label"
                style={{ width: '100%', fontFamily: '"Outfit", sans-serif' }}
                isSearchable
                isLoading={loadingEntityLists}
                onChange={(value) => {
                setChanges((prev) => ({ ...prev, assetType: true }));
                setForm({ assetType: value });
                dispatch(fleetModalAssetTypeSearchEntityLists({ label: value?.label }));
                }}
            />
            </Form.Item>
    
            <Form.Item
            ref={registerItem}
            name="assetSubtype"
            label={messages['manage_createEditAsset_assetSubtype']}
            rules={[{ required: HasPermission(user, 'evir'), message: messages['required_field'] }]}
            >
            <Select
                id="assetSubtype"
                options={assetSubtypes || []}
                placeholder={messages['select']}
                valueField="id"
                textField="name"
                style={{ width: '100%', fontFamily: '"Outfit", sans-serif' }}
                isSearchable
                isLoading={loadingAssetSubtypes}
                onChange={(value) => change('assetSubtype', value)}
            />
            </Form.Item>
    
            {isVisible(sections.LICENSE_PLATE) && (
            <>
                <Form.Item
                ref={registerItem}
                name="licensePlate"
                label={messages['manage_createEditAsset_licensePlate']}
                className="unit-asset-formItem"
                rules={[
                    { required: isRequired('licensePlate'), message: messages['required_field'] },
                    {
                      validator: (_, value) => {
                        if (!value || value.length === 0) return Promise.resolve();
                        if (value.length < 4 || value.length > 20) {
                          return Promise.reject(new Error(messages['fleetModalUpdateUnit_licensePlateErrorLength'] || 'License Plate must be between 1 and 20 characters'));
                        }
                        return Promise.resolve();
                      }
                    }
                ]}
                >
                <Input
                    id="licensePlate"
                    type="text"
                    placeholder={messages['manage_createEditAsset_licensePlate']}
                    onChange={(value) => {
                        const cleanedValue = cleanLicensePlate(value);
                        change('licensePlate', cleanedValue);
                        if (cleanedValue !== value) {
                            setTimeout(() => {
                                unitFormRef.setValues({ licensePlate: cleanedValue });
                            }, 100);
                        }
                    }}
                    maxLength={20}
                />
                </Form.Item>
                <CounterString string={formData?.licensePlate || ''} maxLength={20} />
            </>
            )}
    
            {isVisible(sections.MAINTENANCE) && (
            <>
                <span style={{ fontWeight: 500, fontSize: 15 }}>{messages['manage_createEditAsset_maintenance']}</span>
                <Divider style={{ marginBottom: 10 }} />
                <Form.Item
                ref={registerItem}
                name="maintenanceCompany"
                label={messages['manage_createEditAsset_maintenanceCompany']}
                rules={[{ required: isRequired('maintenanceCompany'), message: messages['required_field'] }]}
                className="unit-asset-formItem"
                >
                <Select
                    id="maintenanceCompany"
                    options={[
                      { id: 'No Maintenance Company', name: messages['fleetModalUpdateUnit_maintenanceCompany_none'] },
                      { id: 'Idealease', name: messages['fleetModalUpdateUnit_maintenanceCompany_idealease'] },
                      { id: 'Nationalease', name: messages['fleetModalUpdateUnit_maintenanceCompany_nationalease'] },
                      { id: 'Noregon', name: messages['fleetModalUpdateUnit_maintenanceCompany_noregon'] },
                      { id: 'Penske', name: messages['fleetModalUpdateUnit_maintenanceCompany_penske'] },
                      { id: 'Ryder', name: messages['fleetModalUpdateUnit_maintenanceCompany_ryder'] },
                      { id: 'Other Maintenance Company', name: messages['fleetModalUpdateUnit_maintenanceCompany_other'] },
                    ]}
                    placeholder={messages['manage_createEditAsset_maintenanceCompany']}
                    valueField="id"
                    textField="name"
                    style={{ width: '100%', fontFamily: '"Outfit", sans-serif' }}
                    isSearchable
                    isClearable
                    onChange={(value) => change('maintenanceCompany', value)}
                />
                </Form.Item>
            </>
            )}
    
            {isVisible(sections.REGISTRATION) && (
            <>
                <span style={{ fontWeight: 500, fontSize: 15 }}>{messages['manage_createEditAsset_registration']}</span>
                <Divider style={{ marginBottom: 10 }} />
                <Grid container spacing={2}>
                <Grid item xs={6}>
                    <Form.Item
                    ref={registerItem}
                    name="registrationCountry"
                    label={messages['manage_createEditAsset_registrationCountry']}
                    rules={[{ required: HasPermission(user, 'zlogs'), message: messages['required_field'] }]}
                    >
                    <Select
                        id="registrationCountry"
                        options={countryOptions || []}
                        placeholder={messages['manage_createEditAsset_registrationCountry']}
                        valueField="entityId"
                        textField="label"
                        style={{ width: '100%', fontFamily: '"Outfit", sans-serif' }}
                        isSearchable
                        isLoading={loadingEntityLists}
                        onChange={(value) => {
                        const isSpecial = ['CO', 'PE', 'NONE'].includes(value?.entityId);
                        const newState = isSpecial
                            ? stateOptions?.find((item) => item?.parentId == value?.entityId) ?? null
                            : null;
    
                        setChanges((prev) => ({
                            ...prev,
                            registrationCountry: true,
                            registrationState: true,
                        }));
    
                        setForm({
                            registrationCountry: value,
                            registrationState: newState,
                        });
                        }}
                    />
                    </Form.Item>
                </Grid>
    
                <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Form.Item
                        ref={registerItem}
                        name="registrationState"
                        label={messages['manage_createEditAsset_registrationState']}
                        rules={[{ required: HasPermission(user, 'zlogs')  || formData?.registrationCountry?.entityId!=="NONE", message: messages['required_field'] }]}
                        style={{ flex: 1, marginBottom: 0 }}
                        >
                        <Select
                            key={registrationStateKey}
                            id="registrationState"
                            options={
                            formData?.registrationCountry
                                ? stateOptions?.filter(
                                    (item) => item?.parentId == formData?.registrationCountry?.entityId
                                )
                                : stateOptions || []
                            }
                            placeholder={messages['manage_createEditAsset_registrationState']}
                            valueField="entityId"
                            textField="label"
                            style={{ width: '100%', fontFamily: '"Outfit", sans-serif' }}
                            isSearchable
                            isLoading={loadingEntityLists}
                            onChange={(value) => change('registrationState', value)}
                            isDisabled={['CO', 'PE', 'NONE'].includes(formData?.registrationCountry?.entityId)}
                        />
                        </Form.Item>
                        {['CO', 'PE', 'NONE'].includes(formData?.registrationCountry?.entityId) && (
                            <Tooltip
                                placement="top"
                                title={messages['manage_createEditAsset_stateAutoSelected'] || 'State is automatically selected based on country'}
                                style={{ marginTop: 20 }}
                            >
                                <Icons name="info" size="small" />
                            </Tooltip>
                        )}
                    </Box>
                </Grid>
                </Grid>
    
                <Form.Item
                ref={registerItem}
                name="registrationExpiration"
                label={messages['manage_createEditAsset_registrationExpiration']}
                rules={[{ required: isRequired('registrationExpiration'), message: messages['required_field'] }]}
                >
                <DatePicker
                    id="registrationExpiration"
                    showButtons
                    isClearable
                    showTimePicker={false}
                    calendarPosition="left"
                    placeholder={messages['manage_createEditAsset_registrationExpiration']}
                    format={GetFormatForMoment(user?.dateformat)}
                    onChange={(value) => {
                    const dateValue = value ? new Date(value) : null;
                    setChanges((prev) => ({ ...prev, registrationExpiration: true }));
                    setForm({ registrationExpiration: dateValue });
                    }}
                    minDate={new Date('2024-01-01T00:00:00')}
                />
                </Form.Item>
            </>
            )}
            {isVisible(sections.CHASSIS_SERIAL_NUMBER) && formData?.assetType?.label?.toUpperCase() !== 'STANDARD' && (
                        <>
                          <Form.Item
                            ref={registerItem}
                            name="chassisSerialNumber"
                            label={messages['manage_createEditAsset_chassisSerialNumber']}
                            className="asset-formItem"
                            rules={[{ required: isRequired('chassisSerialNumber'), message: messages['required_field'] }]}
                          >
                            <InputComponent
                              id="chassisSerialNumber"
                              type="text"
                              placeholder={messages['manage_createEditAsset_chassisSerialNumber']}
                              maxLength={30}
                              regex={/^[A-Za-z0-9]*$/} 
                              className="asset-text-uppercase"
                              onChange={(value) => {
                                const raw = (value ?? '').toString();
                                const cleaned = raw.replace(/\s+/g, '').toUpperCase();
                                change('chassisSerialNumber', cleaned);
                              }}
                            />
                          </Form.Item>
                          <CounterString string={formData?.chassisSerialNumber || ''} maxLength={30} />
                        </>
                      )}
                
                      {isVisible(sections.ENGINE_SERIAL_NUMBER) && (
                        <>
                          {/* Hide for Trailer/Dolly asset types */}
                          {!['TRAILER', 'DOLLY'].includes(formData?.assetType?.label?.toUpperCase()) && (
                            <>
                              <Form.Item
                                ref={registerItem}
                                name="engineSerialNumber"
                                label={messages['manage_createEditAsset_engineSerialNumber']}
                                className="asset-formItem"
                                rules={[{ required: isRequired('engineSerialNumber'), message: messages['required_field'] }]}
                              >
                                <InputComponent
                                  id="engineSerialNumber"
                                  type="text"
                                  placeholder={messages['manage_createEditAsset_engineSerialNumber']}
                                  className="asset-text-uppercase"
                                  maxLength={30}
                                  regex={/^[A-Za-z0-9]*$/} 
                                  onChange={(value) => {
                                    const raw = (value ?? '').toString();
                                    const cleaned = raw.replace(/\s+/g, '').toUpperCase();
                                    change('engineSerialNumber', cleaned);
                                  }}
                                />
                              </Form.Item>
                              <CounterString string={formData?.engineSerialNumber || ''} maxLength={30} />
                            </>
                          )}
                        </>
                      )}
    
            {isVisible(sections.VEHICLE_ID) && (
            <>
                <span style={{ fontWeight: 500, fontSize: 15 }}>{messages['manage_createEditAsset_vehicleId']}</span>
                <Divider style={{ marginBottom: 10 }} />
                {(formData.jbus || formData.vbus) ? (
                    <div className="switchEcuVin">
                      <Switch
                        id="vinEcu"
                        optionLabels={["ECU", "ECU"]}
                        size={"medium"}
                        checked={Boolean(formData.useEcuVin) || false}
                        isDisabled={!formData.jbus && !formData.vbus}
                        onChange={(checked) => {
                          const ecuVin = formData?.ecuVin || unit?.lastReading?.ecuVin || unit?.asset?.ecuVin || "";
                          const currentVin = formData?.vin || "";

                          if (checked) {
                            manualVinRef.current = currentVin;
                            if (ecuVin && ecuVin !== currentVin) {
                              change("vin", ecuVin);
                            }
                          } else {
                            const fallbackVin = manualVinRef.current || unit?.asset?.vin || currentVin;
                            if (fallbackVin !== currentVin) {
                              change("vin", fallbackVin);
                            }
                          }

                          onEcuChange(checked, "useEcuVin")
                          if (checked && ecuVin) {
                            dispatch(fleetModalDecodeVin({ vin: ecuVin }))
                          }
                        }}
                      />
                    </div>
                ) : <></>}
                <Form.Item
                ref={registerItem}
                name="vin"
                label={messages['manage_createEditAsset_vin']}
                className="unit-asset-formItem"
                rules={[
                  { required: isRequired('vin'), message: messages['required_field'] },
                  {
                    validator: (_, value) => {
                      if (!value || value.length === 0) return Promise.resolve();
                      // VIN warning: debe tener 17 caracteres y no contener I, O, o Q
                      const isValidLength = value.length === 17;
                      const hasInvalidChars = /[IOQ]/i.test(value);
                      if (!isValidLength || hasInvalidChars) {
                        // Warning level - permite guardar pero muestra advertencia
                        return Promise.resolve();
                      }
                      return Promise.resolve();
                    },
                    warningOnly: true
                  }
                ]}
                >
                <Input
                    id="vin"
                    type="text"
                    placeholder={messages['manage_createEditAsset_vin']}
                    onChange={(value) => {
                      const cleaned = cleanString(value);
                      const prevVin = (formData?.vin ?? "").toString();

                      change("vin", cleaned);

                      if (cleaned !== prevVin) {
                        clearVinDependentFields();
                    }
                    }}
                    maxLength={17}
                    buttonText={loadingDecodeVin ? messages['loading'] || 'Loading...' : messages['manage_createEditAsset_decodeVIN']}
                    onClick={() => {
                    const values = unitFormRef?.ref?.current?.getValues();
                    if (values?.vin && !loadingDecodeVin) {
                        dispatch(fleetModalDecodeVin({ vin: values?.vin }));
                    }
                    }}
                    className="unit-asset-vin-input"
                    isDisabled={Boolean(formData.useEcuVin)}
                />
                </Form.Item>
                <CounterString string={formData?.vin || ''} maxLength={17} />
    
                <Form.Item
                ref={registerItem}
                name="make"
                label={messages['manage_createEditAsset_make']}
                className="unit-asset-formItem"
                rules={[{ required: isRequired('make'), message: messages['required_field'] }]}
                >
                <Input
                    id="make"
                    type="text"
                    placeholder={messages['manage_createEditAsset_make']}
                    onChange={(value) => {
                        const cleanedValue = cleanString(value);
                        change('make', cleanedValue);
                        if (cleanedValue !== value) {
                            setTimeout(() => {
                                unitFormRef.setValues({ make: cleanedValue });
                            }, 100);
                        }
                    }}
                    maxLength={30}
                />
                </Form.Item>
                <CounterString string={formData?.make || ''} maxLength={30} />
    
                <Form.Item
                ref={registerItem}
                name="model"
                label={messages['manage_createEditAsset_model']}
                className="unit-asset-formItem"
                rules={[{ required: isRequired('model'), message: messages['required_field'] }]}
                >
                <Input
                    id="model"
                    type="text"
                    placeholder={messages['manage_createEditAsset_model']}
                    onChange={(value) => {
                        const cleanedValue = cleanString(value);
                        change('model', cleanedValue);
                        if (cleanedValue !== value) {
                            setTimeout(() => {
                                unitFormRef.setValues({ model: cleanedValue });
                            }, 100);
                        }
                    }}
                    maxLength={50}
                />
                </Form.Item>
                <CounterString string={formData?.model || ''} maxLength={50} />
    
                <Form.Item
                ref={registerItem}
                name="year"
                label={messages['manage_createEditAsset_year']}
                rules={[
                    { max: nextYear, message: `${messages['manage_createEditAsset_maxValue']}: ${nextYear}` },
                    { min: 1980, message: `${messages['manage_createEditAsset_minValue']}: 1980` },
                    { required: isRequired('year'), message: messages['required_field'] },
                ]}
                className="unit-asset-formItem"
                >
                <InputNumeric
                    id="year"
                    type="number"
                    isInteger
                    placeholder={messages['manage_createEditAsset_year']}
                    onChange={(value) => {
                    const n = Math.abs(Number(value));
                    change('year', n);
                    }}
                    maxLength={4}
                    onlyPositive
                    min={1980}
                    max={nextYear}
                />
                </Form.Item>
                <CounterString string={formData?.year || ''} maxLength={4} />
    
                {/* Hide Engine Liters for Trailer/Dolly asset types */}
                {!['TRAILER', 'DOLLY'].includes(formData?.assetType?.label?.toUpperCase()) && (
                  <>
                    <Form.Item
                    ref={registerItem}
                    name="engine"
                    label={messages['manage_createEditAsset_engineLiters']}
                    rules={[
                        { max: 10000, message: `${messages['max']}: 10000` },
                        { required: isRequired('engine'), message: messages['required_field'] },
                    ]}
                    className="unit-asset-formItem"
                    >
                    <InputNumeric
                        id="engine"
                        type="number"
                        placeholder={messages['manage_createEditAsset_engineLiters']}
                        onChange={(value) => {
                        const n = parseFloat(String(value));
                        const normalized = !isFinite(n) || n <= 0 ? null : n;
                        change('engine', normalized);
                        }}
                        maxLength={5}
                        onlyPositive
                        decimalPlaces={2}
                    />
                    </Form.Item>
                    <CounterString string={formData?.engine || ''} maxLength={5} />
                  </>
                )}
            </>
            )}
    
            {isVisible(sections.MPG) && (
            <>
                {/* Hide MPG for Trailer/Dolly asset types */}
                {!['TRAILER', 'DOLLY'].includes(formData?.assetType?.label?.toUpperCase()) && (
                    <>
                        <span style={{ fontWeight: 500, fontSize: 15 }}>{messages['manage_createEditAsset_mpg']}</span>
                        <Divider style={{ marginBottom: 10 }} />
    
                <Form.Item
                ref={registerItem}
                name="mpgHighway"
                label={messages['manage_createEditAsset_highway']}
                rules={[
                    { max: 200, message: `${messages['max']}: 200` },
                    { required: isRequired('mpgHighway'), message: messages['required_field'] },
                ]}
                className="unit-asset-formItem"
                >
                <InputNumeric
                    id="mpgHighway"
                    type="number"
                    isInteger
                    placeholder={messages['manage_createEditAsset_highway']}
                    onChange={(value) => {
                    const n = Math.abs(Number(value));
                    change('mpgHighway', n);
                    }}
                    maxLength={3}
                    onlyPositive
                />
                </Form.Item>
                <CounterString string={formData?.mpgHighway || ''} maxLength={3} />
    
                <Form.Item
                ref={registerItem}
                name="mpgCity"
                label={messages['manage_createEditAsset_city']}
                rules={[
                    { max: 200, message: `${messages['max']}: 200` },
                    { required: isRequired('mpgCity'), message: messages['required_field'] },
                ]}
                className="unit-asset-formItem"
                >
                <InputNumeric
                    id="mpgCity"
                    type="number"
                    isInteger
                    placeholder={messages['manage_createEditAsset_city']}
                    onChange={(value) => {
                    const n = Math.abs(Number(value));
                    change('mpgCity', n);
                    }}
                    maxLength={3}
                    onlyPositive
                />
                </Form.Item>
                <CounterString string={formData?.mpgCity || ''} maxLength={3} />
                    </>
                )}
            </>
            )}
    
            {isVisible(sections.EVIR) && (
            <>
                {HasPermission(user, 'evir') ? (
                <>
                    <span style={{ fontWeight: 500, fontSize: 15 }}>{messages['manage_createEditAsset_evir']}</span>
                    <Divider style={{ marginBottom: 10 }} />
    
                    <Form.Item
                    ref={registerItem}
                    name="inspectionDeviceType"
                    label={messages['manage_createEditAsset_inspectionDeviceType']}
                    rules={[{ required: isRequired('inspectionDeviceType'), message: messages['required_field'] }]}
                    >
                    <Select
                        id="inspectionDeviceType"
                        options={entityLists?.filter((item) => item?.entityName === 'InspectionDeviceType') || []}
                        placeholder={messages['select']}
                        valueField="entityId"
                        textField="label"
                        style={{ width: '100%', fontFamily: '"Outfit", sans-serif' }}
                        isSearchable
                        isLoading={loadingEntityLists}
                        onChange={(value) => change('inspectionDeviceType', value)}
                        isClearable
                    />
                    </Form.Item>
    
                    <Form.Item
                    ref={registerItem}
                    name="inspectionTagId"
                    label={messages['manage_createEditAsset_inspectionTagId']}
                    className="unit-asset-formItem"
                    rules={[
                        { required: isRequired('inspectionTagId'), message: messages['required_field'] },
                        { min: 1, message: messages['manage_createEditAsset_minValue'] ? `${messages['manage_createEditAsset_minValue']}: 1` : 'Minimum value: 1' },
                        { max: 999999999, message: messages['max'] ? `${messages['max']}: 999,999,999` : 'Maximum value: 999,999,999' }
                    ]}
                    >
                    <InputNumeric
                        id="inspectionTagId"
                        type="number"
                        placeholder={messages['manage_createEditAsset_inspectionTagId']}
                        onChange={(value) => {
                        const n = Math.abs(Number(value));
                        change('inspectionTagId', n);
                        }}
                        maxLength={9}
                        onlyPositive
                        min={1}
                        max={999999999}
                    />
                    </Form.Item>
                    <CounterString string={formData?.inspectionTagId || ''} maxLength={9} />
                </>
                ) : null}
            </>
            )}
    
            {isVisible(sections.ZONAR_ELD) && (
            <>
                {HasPermission(user, 'zlogs') && !['TRAILER', 'DOLLY'].includes(formData?.assetType?.label?.toUpperCase()) ? (
                <>
                    <span style={{ fontWeight: 500, fontSize: 15 }}>Zonar ELD</span>
                    <Divider style={{ marginBottom: 10 }} />
                    <div style={{ height: 100 }}>
                    <Form.Item
                    ref={registerItem}
                        name="powerUnitNumber"
                        label={messages['manage_createEditAsset_powerUnitNumber']}
                        rules={[{ required: isRequired('powerUnitNumber'), message: messages['required_field'] }]}
                        className="unit-asset-formItem"
                    >
                        <Input
                        id="powerUnitNumber"
                        type="text"
                        placeholder={messages['manage_createEditAsset_powerUnitNumber']}
                        onChange={(value) => {
                            const cleanedValue = cleanString(value);
                            change('powerUnitNumber', cleanedValue);
                            if (cleanedValue !== value) {
                                setTimeout(() => {
                                    unitFormRef.setValues({ powerUnitNumber: cleanedValue });
                                }, 100);
                            }
                        }}
                        maxLength={10}
                        minLength={4}
                        />
                    </Form.Item>
                    <CounterString string={formData?.powerUnitNumber || ''} maxLength={10} />
                    </div>
                </>
                ) : null}
            </>
            )}
        </div>
    );
}

export default Asset;